import { getAppRouteMeta } from '@/routing/router-static-data';
import type { AppRouteMeta } from '@/routing/route-meta';

export type RouterNavEntry = {
  id: string;
  fullPath: string;
  meta: AppRouteMeta;
};

/**
 * A minimal route interface for navigation extraction.
 * Using this instead of AnyRoute avoids complex type variance issues
 * with TanStack Router's strict generics.
 */
type RouteForNav = {
  id?: unknown;
  fullPath?: unknown;
  options?: { staticData?: unknown };
  children?: unknown;
};

function isRouteForNav(value: unknown): value is RouteForNav {
  return typeof value === 'object' && value !== null;
}

/**
 * Maps a single route to a nav entry, or returns null if it doesn't declare a
 * visible nav label.
 */
function toNavEntry(route: RouteForNav): RouterNavEntry | null {
  const meta = getAppRouteMeta(route);

  if (
    meta?.nav?.label &&
    !meta.nav.hidden &&
    'id' in route &&
    'fullPath' in route &&
    typeof route.id === 'string' &&
    typeof route.fullPath === 'string'
  ) {
    return { id: route.id, fullPath: route.fullPath, meta };
  }

  return null;
}

/**
 * Collects nav entries from a flat list of routes (e.g. `router.flatRoutes`).
 *
 * Preferred over {@link extractNavEntriesFromRouteTree}: the router instance's
 * flattened routes are processed once at `router.init()` and remain stable
 * across HMR, whereas the raw `routeTree` export can lose its assembled
 * `children` when an importing module is hot-reloaded.
 */
export function extractNavEntriesFromRoutes(
  routes: readonly RouteForNav[],
): RouterNavEntry[] {
  const out: RouterNavEntry[] = [];
  for (const route of routes) {
    const entry = toNavEntry(route);
    if (entry) out.push(entry);
  }
  return out;
}

function getChildren(route: RouteForNav): RouteForNav[] {
  const { children } = route;
  if (!children) return [];

  if (Array.isArray(children)) {
    return children.filter(isRouteForNav);
  }

  if (typeof children === 'object') {
    return Object.values(children).filter(isRouteForNav);
  }

  return [];
}

/**
 * Collects all routes in the route tree that declare `staticData.app.nav.label`.
 *
 * This is Phase 2 groundwork to generate sidebar navigation from TanStack Router.
 */
export function extractNavEntriesFromRouteTree(
  routeTree: RouteForNav,
): RouterNavEntry[] {
  const out: RouterNavEntry[] = [];

  const visit = (route: RouteForNav) => {
    const entry = toNavEntry(route);
    if (entry) out.push(entry);

    for (const child of getChildren(route)) {
      visit(child);
    }
  };

  visit(routeTree);

  return out;
}

export type SidebarNavGroup = {
  /**
   * Stable identity for React keys and collapse state. For custom layouts this
   * is the persisted section id; for the default route-derived grouping it is
   * the group title (unique, since groups are keyed by title here).
   */
  id: string;
  title: string;
  items: RouterNavEntry[];
};

/**
 * Groups entries by `meta.nav.group` and sorts by `meta.nav.order` (default 0).
 */
export function groupNavEntries(entries: RouterNavEntry[]): SidebarNavGroup[] {
  const byGroup = new Map<string, RouterNavEntry[]>();

  for (const entry of entries) {
    const group = entry.meta.nav?.group ?? 'Other';
    const arr = byGroup.get(group) ?? [];
    arr.push(entry);
    byGroup.set(group, arr);
  }

  const groups: SidebarNavGroup[] = Array.from(byGroup.entries()).map(
    ([title, items]) => ({
      id: title,
      title,
      items: items
        .slice()
        .sort((a, b) => (a.meta.nav?.order ?? 0) - (b.meta.nav?.order ?? 0)),
    }),
  );

  // Sort groups by groupOrder (derived from first item), then alphabetically
  groups.sort((a, b) => {
    const ao = a.items[0]?.meta.nav?.groupOrder ?? Infinity;
    const bo = b.items[0]?.meta.nav?.groupOrder ?? Infinity;
    if (ao !== bo) return ao - bo;
    return a.title.localeCompare(b.title);
  });

  return groups;
}
